class VerticalCampaign extends HTMLElement {
  connectedCallback() {
    this.panels = [...this.querySelectorAll("[data-campaign-panel]")];
    this.preview = this.querySelector("[data-campaign-preview]");
    this.previewImage = this.preview?.querySelector("img");
    this.activate = (panel) => {
      this.panels.forEach((item) => item.classList.toggle("is-active", item === panel));
      const image = panel.querySelector("img");
      if (image && this.previewImage) this.previewImage.src = image.currentSrc || image.src;
      const accent = getComputedStyle(panel).getPropertyValue("--panel-accent");
      if (this.preview) this.preview.style.setProperty("--panel-accent", accent);
    };
    this.move = (event) => {
      const rect = this.getBoundingClientRect();
      this.style.setProperty("--preview-x", `${event.clientX - rect.left}px`);
      this.style.setProperty("--preview-y", `${event.clientY - rect.top}px`);
    };
    this.panelEnterHandlers = this.panels.map((panel) => {
      const handler = () => this.activate(panel);
      panel.addEventListener("pointerenter", handler);
      panel.addEventListener("focus", handler);
      return { panel, handler };
    });
    this.addEventListener("pointermove", this.move, { passive: true });
  }

  disconnectedCallback() {
    this.removeEventListener("pointermove", this.move);
    this.panelEnterHandlers?.forEach(({ panel, handler }) => {
      panel.removeEventListener("pointerenter", handler);
      panel.removeEventListener("focus", handler);
    });
  }
}

class EditorialCategoryRail extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector("[data-category-rail]");
    if (!this.rail) return;
    this.dragging = false;
    this.moved = false;
    this.start = 0;
    this.left = 0;
    this.onDown = (event) => {
      if (event.pointerType !== "mouse" || event.button !== 0) return;
      this.dragging = true;
      this.moved = false;
      this.start = event.clientX;
      this.left = this.rail.scrollLeft;
      this.rail.classList.add("is-dragging");
      this.rail.setPointerCapture(event.pointerId);
    };
    this.onMove = (event) => {
      if (!this.dragging) return;
      const distance = event.clientX - this.start;
      if (Math.abs(distance) > 6) this.moved = true;
      this.rail.scrollLeft = this.left - distance * 1.2;
    };
    this.onUp = (event) => {
      this.dragging = false;
      this.rail.classList.remove("is-dragging");
      if (this.rail.hasPointerCapture?.(event.pointerId)) this.rail.releasePointerCapture(event.pointerId);
    };
    this.preventDraggedClick = (event) => {
      if (!this.moved) return;
      event.preventDefault();
      event.stopPropagation();
      this.moved = false;
    };
    this.rail.addEventListener("pointerdown", this.onDown);
    this.rail.addEventListener("pointermove", this.onMove);
    this.rail.addEventListener("pointerup", this.onUp);
    this.rail.addEventListener("pointercancel", this.onUp);
    this.rail.addEventListener("click", this.preventDraggedClick, true);
  }

  disconnectedCallback() {
    if (!this.rail) return;
    this.rail.removeEventListener("pointerdown", this.onDown);
    this.rail.removeEventListener("pointermove", this.onMove);
    this.rail.removeEventListener("pointerup", this.onUp);
    this.rail.removeEventListener("pointercancel", this.onUp);
    this.rail.removeEventListener("click", this.preventDraggedClick, true);
  }
}

class EditorialDepth extends HTMLElement {
  connectedCallback() {
    this.frame = 0;
    this.update = () => {
      this.frame = 0;
      const rect = this.getBoundingClientRect();
      const travel = innerHeight + rect.height;
      const progress = Math.min(1, Math.max(0, (innerHeight - rect.top) / travel));
      this.style.setProperty("--depth-progress", progress.toFixed(4));
    };
    this.request = () => { if (!this.frame) this.frame = requestAnimationFrame(this.update); };
    this.tilt = (event) => {
      if (event.pointerType === "touch") return;
      const rect = this.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      const y = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
      const rotation = Number.parseFloat(getComputedStyle(this).getPropertyValue("--depth-editorial-rotation")) || 4;
      this.style.setProperty("--depth-x", `${((0.5 - y) * rotation * 2).toFixed(2)}deg`);
      this.style.setProperty("--depth-y", `${((x - 0.5) * rotation * 2).toFixed(2)}deg`);
    };
    this.reset = () => { this.style.setProperty("--depth-x", "0deg"); this.style.setProperty("--depth-y", "0deg"); };
    this.update();
    addEventListener("scroll", this.request, { passive: true });
    addEventListener("resize", this.request);
    this.addEventListener("pointermove", this.tilt);
    this.addEventListener("pointerleave", this.reset);
  }

  disconnectedCallback() {
    if (this.frame) cancelAnimationFrame(this.frame);
    removeEventListener("scroll", this.request);
    removeEventListener("resize", this.request);
    this.removeEventListener("pointermove", this.tilt);
    this.removeEventListener("pointerleave", this.reset);
  }
}

class EditorialCollection extends HTMLElement {
  connectedCallback() {
    this.buttons = [...this.querySelectorAll("[data-density-button]")];
    this.handlers = this.buttons.map((button) => {
      const handler = () => {
        const density = button.dataset.densityButton || "3";
        this.style.setProperty("--collection-columns", density);
        this.dataset.density = density;
        this.buttons.forEach((item) => {
          const active = item === button;
          item.classList.toggle("is-selected", active);
          item.setAttribute("aria-pressed", String(active));
        });
      };
      button.addEventListener("click", handler);
      return { button, handler };
    });
  }

  disconnectedCallback() {
    this.handlers?.forEach(({ button, handler }) => button.removeEventListener("click", handler));
  }
}

class EditorialGraphicCursor extends HTMLElement {
  connectedCallback() {
    this.selector = ".vertical-campaign__panel,.editorial-categories__card,.video-campaign,.split-campaign__panel,.depth-editorial-liquid,.product-card__media,.product-media-grid__item,.media-grid__item,.collection-card__media,.hero-editorial,.split-editorial__media,.editorial-footer";
    this.lastTarget = null;
    this.reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
    this.coarsePointer = matchMedia("(hover: none), (pointer: coarse)");
    this.move = (event) => {
      if (event.pointerType === "touch" || this.reducedMotion.matches || this.coarsePointer.matches) return;
      const target = event.target.closest?.(this.selector) || null;
      if (this.lastTarget && this.lastTarget !== target) this.resetTarget(this.lastTarget);
      this.lastTarget = target;
      this.style.setProperty("--cursor-x", `${event.clientX}px`);
      this.style.setProperty("--cursor-y", `${event.clientY}px`);
      document.body.classList.toggle("has-graphic-hover", Boolean(target));
      if (!target) return;
      const rect = target.getBoundingClientRect();
      const x = Math.min(0.5, Math.max(-0.5, (event.clientX - rect.left) / rect.width - 0.5));
      const y = Math.min(0.5, Math.max(-0.5, (event.clientY - rect.top) / rect.height - 0.5));
      target.style.setProperty("--graphic-x", `${(x * 7).toFixed(2)}px`);
      target.style.setProperty("--graphic-y", `${(y * 7).toFixed(2)}px`);
      target.classList.add("graphic-hover-active");
    };
    this.leave = () => {
      if (this.lastTarget) this.resetTarget(this.lastTarget);
      this.lastTarget = null;
      document.body.classList.remove("has-graphic-hover");
    };
    document.addEventListener("pointermove", this.move, { passive: true });
    document.addEventListener("pointerleave", this.leave);
    window.addEventListener("blur", this.leave);
  }

  resetTarget(target) {
    target.classList.remove("graphic-hover-active");
    target.style.removeProperty("--graphic-x");
    target.style.removeProperty("--graphic-y");
  }

  disconnectedCallback() {
    document.removeEventListener("pointermove", this.move);
    document.removeEventListener("pointerleave", this.leave);
    window.removeEventListener("blur", this.leave);
    this.leave?.();
  }
}

const define = (name, constructor) => { if (!customElements.get(name)) customElements.define(name, constructor); };
define("editorial-graphic-cursor", EditorialGraphicCursor);
define("vertical-campaign", VerticalCampaign);
define("editorial-category-rail", EditorialCategoryRail);
define("editorial-depth", EditorialDepth);
define("editorial-collection", EditorialCollection);

const initializeSliders = (root = document) => {
  root.querySelectorAll("[data-slider-controls]").forEach((controls) => {
    if (controls.dataset.sliderReady === "true") return;
    const host = controls.closest("vertical-campaign, editorial-category-rail, section");
    const rail = host?.querySelector("[data-slider-rail]");
    if (!rail) return;
    controls.dataset.sliderReady = "true";
    const buttons = [...controls.querySelectorAll("[data-slider-direction]")];
    const update = () => {
      const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
      buttons.forEach((button) => {
        const direction = Number(button.dataset.sliderDirection) || 1;
        const disabled = direction < 0 ? rail.scrollLeft <= 2 : rail.scrollLeft >= max - 2;
        button.disabled = disabled;
        button.setAttribute("aria-disabled", String(disabled));
      });
    };
    buttons.forEach((button) => {
      button.addEventListener("click", () => {
        const direction = Number(button.dataset.sliderDirection) || 1;
        rail.scrollBy({ left: direction * Math.max(rail.clientWidth * 0.82, 280), behavior: "smooth" });
      });
    });
    rail.addEventListener("scroll", update, { passive: true });
    addEventListener("resize", update);
    requestAnimationFrame(update);
  });
};

const initializeProduct = (root = document) => {
  root.querySelectorAll("[data-product]").forEach((product) => {
    if (product.dataset.editorialReady === "true") return;
    product.dataset.editorialReady = "true";
    const select = product.querySelector("[data-variant-select]");
    const radios = [...product.querySelectorAll(".editorial-option input")];
    radios.forEach((radio) => radio.addEventListener("change", () => {
      if (!select) return;
      const values = radios.filter((item) => item.checked).map((item) => item.value.toLowerCase());
      const match = [...select.options].find((option) => values.every((value) => option.textContent.toLowerCase().includes(value)));
      if (match) {
        select.value = match.value;
        select.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }));
    const closeZoom = () => product.querySelectorAll(".product-media-grid__item.is-zoomed").forEach((item) => {
      item.classList.remove("is-zoomed");
      item.querySelector("[data-product-zoom]")?.setAttribute("aria-pressed", "false");
    });
    product.querySelectorAll("[data-product-zoom]").forEach((button) => button.addEventListener("click", () => {
      const item = button.closest(".product-media-grid__item");
      const zoomed = !item?.classList.contains("is-zoomed");
      closeZoom();
      if (zoomed && item) item.classList.add("is-zoomed");
      button.setAttribute("aria-pressed", String(Boolean(zoomed)));
    }));
    product.addEventListener("keydown", (event) => { if (event.key === "Escape") closeZoom(); });
    select?.dispatchEvent(new Event("change", { bubbles: true }));
  });
};

document.addEventListener("DOMContentLoaded", () => {
  initializeSliders();
  initializeProduct();
});
document.addEventListener("shopify:section:load", (event) => {
  initializeSliders(event.target);
  initializeProduct(event.target);
});